<template>
  <home-floating-window
    :title="$tc('PathInspector')"
    @close="closePathInspector"
    :pos-x="position.x"
    :pos-y="position.y"
  >
    <template v-slot:iconWindows>
      <PathInspectorSVG class="floating-container-svg" />
    </template>

    <template v-slot:objectDivWindows>
      <div class="floating-tool-content">
        <div style="text-align: left; margin: 0">
          <v-btn
            v-if="selectedObjects.length !== 0"
            small
            text
            class="clear-button ma-0"
            @click="clearSelectedObjects"
          >
            <v-icon style="padding-right: 5px">clear_all</v-icon>
            {{ $t("ClearAll") }}
          </v-btn>
          <div v-else style="height: 10%" />
        </div>

        <div class="floating-objects-wrapper">
          <div
            ref="listItem"
            class="floating-object-selected"
            v-for="(object, index) in selectedObjects"
            :key="object.uuid"
          >
            <COSVG
              v-if="object.type.toLowerCase() === 'central office'"
              class="floating-img"
            />
            <PNSVG
              v-else-if="object.type.toLowerCase() === 'primary node'"
              class="floating-img"
            />
            <TobyBoxSVG
              v-else-if="object.type.toLowerCase() === 'toby box'"
              class="floating-img"
            />
            <img
              v-else
              class="floating-img"
              :src="
                require(`@/assets/objectIcons/${object.type.toLowerCase()}.png`)
              "
            />

            <div
              class="floating-between-objects"
              v-if="object.uuid !== selectedObjects[0].uuid"
              style="border-color: #4fa2b7"
            />

            <span style="text-align: left; padding-right: 5px">
              {{ objectIdentification(object) }}
            </span>

            <!--The image does not appear, but you can open the menu-->
            <div
              class="floating-hover-options"
              @click="openObjectMenu($event, object,index)"
            >
              <v-icon>more_vert</v-icon>
            </div>
          </div>
        </div>

        <HighlightedBox
          :bad-object="badObject"
          :is-visible="true"
          :message="getMessageForHighlightedBox()"
          :state="state"
          :loading="loading"
          :class="{ 'mt-0': selectedObjects.length === 0 }"
        />
      </div>

      <window-object-menu
        v-if="objectMenu.open"
        :event="objectMenu.event"
        :object="objectMenu.object"
        :actionFrom="selectedToolObjectLayer"
        @updateGeoServer="updateGeoServer"
        @closeMenu="closeObjectMenu"
        @openForm="openFormFromObjectMenu"
        @openDetailForm="openFormFromObjectMenu('detail')"
        @removeFromCable="removeObjectFrom"
        v-on-clickaway="closeObjectMenu"
        :style="{top:getTopObjectMenu, left: getLeftObjectMenu}"
      />
    </template>
  </home-floating-window>
</template>

<script>
import HomeFloatingWindow from "@/components/Home/Content/ObjectWindow/HomeFloatingWindow";
import PathInspectorSVG from "@/assets/home/toolbars/pathInspector.svg";
import TobyBoxSVG from "@/assets/objectIcons/tobybox.svg";
import COSVG from "@/assets/objectIcons/central office.svg";
import PNSVG from "@/assets/objectIcons/primary node.svg";
import { mapActions } from "vuex";
import HomeWindowButton from "@/components/Home/Content/HomeWindowButton";
import { GeoJSON, WKT } from "ol/format";
import { bus } from "@/main";
import { Feature } from "ol";
import { Point } from "ol/geom";
import { Stroke, Style } from "ol/style";
import HighlightedBox from "@/components/_Molecules/HighlightedBox";
import { mixin as clickaway } from "vue-clickaway";
import WindowObjectMenu from "@/components/Home/Content/ObjectMenu/WindowObjectMenu";

export default {
  name: "PathInspectorToolbar",

  mixins: [clickaway],

  components: {
    WindowObjectMenu,
    HighlightedBox,
    HomeWindowButton,
    HomeFloatingWindow,
    PathInspectorSVG,
    TobyBoxSVG,
    PNSVG,
    COSVG
  },

  props: {
    vectorSource: {},
    position: Object
  },

  data() {
    return {
      selectedObjects: [],
      nodeToSend: [],
      state: "selected",

      badObject: false,

      loading: false,

      objectMenu: {
        open: false,
        event: null,
        object: null,
        top: null,
        left: null
      }
    };
  },

  computed: {
    selectedToolObjectLayer() {
      return this.$store.getters.tools_selectedObjectLayer;
    },
    getTopObjectMenu(){
      return this.objectMenu.top;
    },
    getLeftObjectMenu(){
      return this.objectMenu.left;
    }
  },

  mounted() {
    bus.$on("object-path-inspector", selectedObject => {
        this.selectedObjects.push(selectedObject);
        this.nodeToSend.push(this.prepareNodeToSend(selectedObject));
      if (this.selectedObjects.length > 1) {
        this.runPathInspector();
      }
    });

    bus.$on("highlight-path-inspector-instructions", () =>
      this.highlightCableBox()
    );

    bus.$on("close-windows-path-inspector", () => this.clearSelectedObjects());
  },

  destroyed() {
    bus.$off("object-path-inspector");
    bus.$off("highlight-path-inspector-instructions");
    bus.$off("close-windows-path-inspector");
  },

  methods: {
    ...mapActions([
      "SET_TOOL_ACTION",
      "BEST_ROUTE",
      "fetchObjectByUUID",
      "UPDATE_SELECTED_OBJECT_IDENTIFIERS",
      "UPDATE_SELECTED_OBJECT_DATA",
      "SET_TOOL_OBJECT_LAYER",
      "setOpenHomeSidebar"
    ]),

    /**
     * Emits an event to close the window of this component
     */
    closePathInspector() {
      this.clearSelectedObjects();
      this.SET_TOOL_ACTION("");
      this.SET_TOOL_OBJECT_LAYER("");
      this.setOpenHomeSidebar("");
      this.$emit("close");
    },

    /**
     * It will check the continuity between the objects
     */
    runPathInspector() {
        this.loading = true;

        this.$nextTick(() => {
          this.BEST_ROUTE({ node: this.nodeToSend, projection: 3857 })
            .then(resp => {
              this.state = "success";

              this.vectorSource.clear();

              let pathways = resp.data.data.pathways;

              if (Array.isArray(pathways)) {
                for (const pathway of pathways) {
                  let features = new GeoJSON().readFeatures(
                    pathway.pw_geo_json,
                    { featureProjection: "EPSG:3857" }
                  );

                  let feature = features[0];
                  let style = new Style({
                    stroke: new Stroke({ color: "#4fa2b7", width: 3 })
                  });
                  feature.setStyle(style);
                  this.vectorSource.addFeature(feature);
                }

                this.updateGeoServer();
              }

            })
            .catch(() => {
              this.state = "fail";
            })
            .then(() => {
              this.loading = false;
            });
        });
    },

    updateGeoServer() {
      this.$emit("updateGeoserver");
    },

    /**
     * Returns final object to send to the BE
     */
    prepareNodeToSend(selectedObject) {
      let feature = new Feature({
        geometry: new Point(selectedObject.coordinates)
      });
      let wkt = new WKT().writeFeature(feature);

      return {
        layerName: selectedObject.layerName,
        text_geom: wkt,
        type: selectedObject.objectType,
        uuid: selectedObject.uuid
      };
    },

    /**
     * Returns the id or label of the selected objects
     */
    objectIdentification(object) {
      let label = "";
      if (object.label) {
        label += object.label;
      } else {
        label += object.id;
      }
      return label;
    },

    clearSelectedObjects() {
      this.state = "selected";
      this.selectedObjects = [];
      this.nodeToSend = [];
      this.$emit("clear");
    },

    /**
     * Returns the message to the highlighted box
     *
     */
    getMessageForHighlightedBox() {
      if (this.loading) {
        return "";
      } else if (this.state === "selected") {
        return this.$i18n.t("SelectClosureTPBuildingTLOrTS");
      } else if (this.state === "success") {
        return this.$i18n.t("SuccessPathInspector");
      } else if (this.state === "fail") {
        return this.$i18n.t("FailPathInspector");
      }
    },

    highlightCableBox() {
      this.badObject = true;
      setTimeout(() => {
        this.badObject = false;
      }, 550);
    },

    ///////////////////////////////
    /*        Object Menu        */
    ///////////////////////////////

    openObjectMenu(ev, object, index) {
      const listItem = this.$refs.listItem[index];
      const position = listItem.getBoundingClientRect();
      let top=position.bottom-position.top + 68 +(position.height * (index));
      let left=position.right-position.left + 25;
      this.objectMenu.top=(top)+"px";
      this.objectMenu.left=(left)+"px";
      setTimeout(() => {
        this.objectMenu.open = true;
        this.objectMenu.event = ev;
        this.objectMenu.object = object;
      }, 0);
    },

    closeObjectMenu() {
      this.objectMenu.open = false;
    },

    openFormFromObjectMenu(action) {
      this.closeObjectMenu();
      this.getObjectDataFromMenu(this.objectMenu.object, action);
    },

    getObjectDataFromMenu(objectIdentifiers, formType) {
      let endpoint = objectIdentifiers.endpoint;
      let uuid = objectIdentifiers.uuid;
      let fields = this.getFields(endpoint);
      let layerName = objectIdentifiers.layerName;

      this.fetchObjectByUUID({
        endpoint: endpoint,
        objectUUID: uuid,
        fieldsToInclude: fields
      })
        .then(resp => {
          this.UPDATE_SELECTED_OBJECT_IDENTIFIERS(objectIdentifiers);
          this.UPDATE_SELECTED_OBJECT_DATA(resp.data.data[0]);
          if (formType === "detail") {
            this.$emit("openForm", "Detail", "edit");
          } else {
            this.$emit("openForm", layerName, formType);
          }
        })
        .catch(error => {
          this.$toasted.global.toast_error(error.response.data.data);
          console.log(error);
        });
    },

    getFields(layer) {
      switch (layer) {
        case "closures":
          return "technicalpoint,fiberterminalbox";
        case "technicalpoints":
          return "images";
        case "buildings":
          return "images,technicalsite,technicallocals";
        case "technicallocals":
          return "technicalsite";
        case "technicalsites":
          return "technicallocals,images";
        default:
          return "";
      }
    },

    removeObjectFrom(uuid) {
      this.closeObjectMenu();
      let index = this.selectedObjects.findIndex(point => point.uuid === uuid);
      if (index > -1) {
        if (this.selectedObjects.length < 2) {
          this.nodeToSend.splice(index, 1);
          this.selectedObjects.splice(index, 1);

          return;
        }
        this.nodeToSend.splice(index, 1);
        this.selectedObjects.splice(index, 1);
        this.vectorSource.clear();
        this.updateGeoServer();
        this.state = "selected";
      }
    }
  }
};
</script>

<style scoped>
.clear-button {
  color: #515358;
  font-size: 12px;
  text-transform: none;
  left: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Toolbars/PathInspectorToolbar.vue